import React, { useState } from "react";
import { Avatar } from "../Avatar";
import { SecondaryButton } from "../Button";
import { sortSubscribersByName } from "./utils";
import { SubscribersSelectorModal } from "./components/SubscribersSelectorModal";
import { createTestId } from "../TestableElement";
import type { SubscribersSelector } from "./SubscribersSelector";

export namespace CurrentSubscriptions {
  export interface Props {
    subscribers: SubscribersSelector.Subscriber[];
    subscribedPeople: SubscribersSelector.Subscriber[];
    isCurrentUserSubscribed: boolean;
    resourceName: string;
    onSubscribe: () => void;
    onUnsubscribe: () => void;
    onEditSubscribers: (subscriberIds: string[]) => void;
    isSubscribeLoading?: boolean;
    isUnsubscribeLoading?: boolean;
    canEditSubscribers: boolean;
  }
}

export function CurrentSubscriptions({
  subscribers,
  subscribedPeople,
  isCurrentUserSubscribed,
  resourceName,
  onSubscribe,
  onUnsubscribe,
  onEditSubscribers,
  isSubscribeLoading = false,
  isUnsubscribeLoading = false,
  canEditSubscribers,
}: CurrentSubscriptions.Props) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleSaveSubscribers = (selected: SubscribersSelector.Subscriber[]) => {
    const subscriberIds = selected.map((s) => s.person?.id).filter((id): id is string => !!id);
    onEditSubscribers(subscriberIds);
  };

  const sortedSubscribers = sortSubscribersByName(subscribedPeople);
  const label = buildLabel(subscribedPeople.length, resourceName);

  return (
    <div>
      <CurrentSubscribersSection
        label={label}
        sortedSubscribers={sortedSubscribers}
        canEditSubscribers={canEditSubscribers}
        setIsModalOpen={setIsModalOpen}
      />

      <div className="mt-4">
        {isCurrentUserSubscribed ? (
          <UnsubscribeSection
            resourceName={resourceName}
            onUnsubscribe={onUnsubscribe}
            isLoading={isUnsubscribeLoading}
          />
        ) : (
          <SubscribeSection onSubscribe={onSubscribe} isLoading={isSubscribeLoading} />
        )}
      </div>

      <SubscribersSelectorModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        subscribers={subscribers}
        selectedSubscribers={subscribedPeople}
        alwaysNotify={[]}
        onSave={handleSaveSubscribers}
      />
    </div>
  );
}

interface CurrentSubscribersSectionProps {
  label: string;
  sortedSubscribers: SubscribersSelector.Subscriber[];
  canEditSubscribers: boolean;
  setIsModalOpen: (open: boolean) => void;
}

function CurrentSubscribersSection({
  label,
  sortedSubscribers,
  canEditSubscribers,
  setIsModalOpen,
}: CurrentSubscribersSectionProps) {
  const noSubscribers = !sortedSubscribers || sortedSubscribers.length < 1;

  if (noSubscribers && !canEditSubscribers) {
    return null;
  }

  return (
    <>
      <div className="font-bold text-sm sm:text-[16px]">Подписчики</div>
      <div className="text-xs sm:text-sm mt-1">{label}</div>
      <div className="flex items-center gap-1 mt-2 flex-wrap gap-y-2">
        {sortedSubscribers
          .filter((s) => s.person)
          .map((s, idx) => (
            <Avatar
              person={s.person!}
              size="tiny"
              key={s.person!.id}
              testId={createTestId("subscriber", s.person?.id || idx.toString())}
            />
          ))}
        {canEditSubscribers && (
          <SecondaryButton onClick={() => setIsModalOpen(true)} size="xs" testId="add-remove-subscribers">Добавить/Удалить людей....</SecondaryButton>
        )}
      </div>
    </>
  );
}

function buildLabel(count: number, resourceName: string): string {
  let prefix: string;

  if (count === 0) {
    prefix = "No one";
  } else if (count === 1) {
    prefix = "1 person";
  } else {
    prefix = `${count} people`;
  }

  return `${prefix} will be notified when someone comments on this ${resourceName}.`;
}

interface SubscribeSectionProps {
  onSubscribe: () => void;
  isLoading: boolean;
}

function SubscribeSection({ onSubscribe, isLoading }: SubscribeSectionProps) {
  return (
    <div>
      <div className="font-bold">Ты не подписан(а)</div>
      <p className="text-sm">Ты не будешь получать уведомления о публикации комментариев.</p>
      <div className="flex mt-2">
        <SecondaryButton onClick={onSubscribe} loading={isLoading} size="xs" testId="subscribe">Подписать меня</SecondaryButton>
      </div>
    </div>
  );
}

interface UnsubscribeSectionProps {
  resourceName: string;
  onUnsubscribe: () => void;
  isLoading: boolean;
}

function UnsubscribeSection({ resourceName, onUnsubscribe, isLoading }: UnsubscribeSectionProps) {
  return (
    <div>
      <div className="font-bold text-sm sm:text-[16px]">Ты подписан(а)</div>
      <p className="text-xs sm:text-sm mt-1">
        You&apos;ll get a notification when someone comments on this {resourceName}.
      </p>
      <div className="flex mt-2">
        <SecondaryButton onClick={onUnsubscribe} loading={isLoading} size="xs" testId="unsubscribe">Отписать меня</SecondaryButton>
      </div>
    </div>
  );
}
